<template>
  <resize-observer :breakpoints="breakpoints">
    <container slot-scope="{ el }" :class="el.is">
      <row class="box-type-row">
        <column :size="el.is === 'small' ? '12' : ''" class="unset-position">
          <sb-field class="destination-countries-field info-field" :class="el.is">
            <sb-label>Origin Country</sb-label>
            <sb-dropdown-search
              v-model="selectedOriginCountry"
              :options="originCountriesOptions"
              :min-search-term-length="0"
              :show-selected-options="true"
              class="destination-countries-search"
              @search="handleFilterDestinationCountries"
            >
              <sb-selected-dropdown-search-option
                slot="selection"
                slot-scope="{ selection }"
                :value="mapOriginSelection(selection)"
                :title="mapOriginSelection(selection)"
                class="pricing-selected-dropdown"
              />
              <sb-dropdown-option slot-scope="{ option }">
                {{ option.name }}
              </sb-dropdown-option>
            </sb-dropdown-search>
            <expand-transition>
              <sb-field-helper-text v-if="showUSZoneMapDownload" class="no-height">
                <a href="https://uiassets-afd.shipbob.com/legacyuiassets/webassets/PricingZoneMaps.pdf" target="_blank" download>Download US zone maps</a>
              </sb-field-helper-text>
              <sb-field-helper-text v-if="showCAZoneMapDownload" class="no-height">
                <a href="https://uiassets-afd.shipbob.com/legacyuiassets/webassets/Canada%20to%20Canada%20Map.pdf" target="_blank" download>Download Canada zone maps</a>
              </sb-field-helper-text>
            </expand-transition>
          </sb-field>
        </column>
        <column v-show="showNetworkOption && showNetworkOptionInitially" :size="el.is === 'small' ? '12' : ''" class="unset-position">
          <sb-field class="info-field" :class="el.is">
            <sb-label>
              Fulfillment Network
              <span>
                <sb-tooltip orientation="top" class="network-overview-tooltip">
                  <span>A set of fulfillment center(s) managed by ShipBob or the merchant. Each group has its own unique pricing plan.</span>
                </sb-tooltip>
                <img :src="InfoIcon" style="margin:0 0 3px 2px;">
              </span>
            </sb-label>
            <sb-dropdown
              v-model="selectedNetwork"
              placeholder="Select Fulfillment Network"
              class="sb-network-dropdown"
              :options="networkOptions"
              :disabled="!hasMerchantPlusFCs"
            >
              <sb-selected-option slot="selection" slot-scope="{ selected }">
                {{ selected.name }}
              </sb-selected-option>
              <nw-options>
                <nw-option slot-scope="{ option }" :value="option">
                  <div class="network-option">
                    <div class="network-option-header">
                      {{ option.name }}
                    </div>
                    <div v-for="fc in option.fulfillmentCenters" :key="fc.fulfillment_center_id" class="network-option-fc-list">
                      {{ fc.fulfillment_center_name }}
                    </div>
                  </div>
                </nw-option>
              </nw-options>
            </sb-dropdown>
          </sb-field>
        </column>
        <column :size="el.is === 'small' ? '12' : ''" class="destination-countries-col unset-position">
          <sb-field class="destination-countries-field info-field" :class="el.is">
            <sb-label>Destination Country</sb-label>
            <sb-dropdown-search
              v-model="selectedDestinationCountries"
              :options="organizedDestinationCountries"
              :min-search-term-length="0"
              :show-selected-options="true"
              :disabled="!hasDestinationCountries"
              class="destination-countries-search"
              @search="handleFilterDestinationCountries"
            >
              <sb-selected-dropdown-search-option
                slot="selection"
                slot-scope="{ selection }"
                :value="mapSelections(selection)"
                :title="mapSelections(selection)"
                class="pricing-selected-dropdown"
              />
              <sb-dropdown-option slot-scope="{ option }">
                {{ option.name }}
              </sb-dropdown-option>
            </sb-dropdown-search>
          </sb-field>
        </column>
        <column :size="el.is === 'small' ? '12' : ''" class="unset-position">
          <sb-field class="info-field" :class="el.is">
            <sb-label>Units of Measure</sb-label>
            <sb-dropdown
              v-model="selectedUnitOfMeasureForm"
              placeholder="Select Pounds or Ounces"
              :options="unitOfMeasureOptions"
            >
              <sb-selected-option slot="selection" slot-scope="{ selected }">
                {{ selected.Name }}
              </sb-selected-option>
              <sb-options>
                <sb-option slot-scope="{ option }" :value="option">
                  {{ option.Name }}
                </sb-option>
              </sb-options>
            </sb-dropdown>
          </sb-field>
        </column>
        <column :size="el.is === 'small' ? '12' : ''" class="unset-position">
          <sb-field class="info-field shippingOptionField" :class="el.is">
            <sb-label>Shipping Option</sb-label>
            <sb-dropdown
              v-model="shippingOption"
              :options="filteredShippingOptions"
              :disabled="!hasShippingOptions"
              placeholder="Select Shipping Option"
              class="shippingOptionDropdown"
            >
              <sb-selected-option slot="selection" slot-scope="{ selected }">
                {{ selected.name }}
              </sb-selected-option>
              <sb-options>
                <sb-option slot-scope="{ option }" :value="option">
                  {{ option.name }}
                </sb-option>
              </sb-options>
            </sb-dropdown>
          </sb-field>
        </column>
      </row>
    </container>
  </resize-observer>
</template>
<script>

import intersection from 'lodash/intersection'
import difference from 'lodash/difference'
import { OUNCES, POUNDS } from '@/mappers/pricing/pricing'
import InfoIcon from '@/components/pricing/assets/images/NetworkInfoIcon.svg'
import NwOptions from './components/NwOptions.vue'
import NwOption from './components/NwOption.vue'

export default {
  name: 'PricingForm',
  components: { NwOption, NwOptions },
  data: () => ({
    breakpoints: {
      sm: el => el.width < 400,
      md: el => el.width >= 400 && el.width < 640,
      lg: el => el.width >= 640 && el.width < 920,
      xl: el => el.width >= 920
    },
    unitOfMeasureOptions: [OUNCES, POUNDS],
    filteredDestinationCountries: [],
    filteredShippingOptions: [],
    showNetworkOptionInitially: false,
    InfoIcon
  }),
  computed: {
    showUSZoneMapDownload () {
      return this.showZoneMapDownload('US')
    },
    showCAZoneMapDownload () {
      return this.showZoneMapDownload('CA')
    },
    hasShippingOptions () {
      return this.shippingOptions.length > 0
    },
    shippingOptions () {
      return this.$shipBobStore.getters['pricing/shippingOptions']
    },
    shippingOption: {
      get () {
        return this.$shipBobStore.getters['pricing/selectedShippingOption']
      },
      set (newShippingOption) {
        this.$shipBobStore.dispatch('pricing/setSelectedShippingOption', newShippingOption)
      }
    },
    hasDestinationCountries () {
      return this.destinationCountries.length > 0
    },
    destinationCountries: {
      get () {
        return this.$shipBobStore.getters['pricing/destinationCountries']
      },
      set () {
        this.$shipBobStore.dispatch('pricing/setDestinationCountries')
      }
    },
    organizedDestinationCountries () {
      const selectedAndFiltered = intersection(this.selectedDestinationCountries, this.filteredDestinationCountries)
      const filteredAndNotSelected = difference(this.filteredDestinationCountries, this.selectedDestinationCountries)
      return selectedAndFiltered.concat(filteredAndNotSelected)
    },
    selectedDestinationCountries: {
      get () {
        return this.$shipBobStore.getters['pricing/selectedDestinationCountries']
      },
      set (newDestinationCountry) {
        this.$shipBobStore.dispatch('pricing/setSelectedDestinationCountries', newDestinationCountry)
      }
    },
    selectedUnitOfMeasure () {
      return this.$shipBobStore.getters['pricing/unitOfMeasure']
    },
    selectedUnitOfMeasureForm: {
      get () {
        return this.$shipBobStore.getters['pricing/unitOfMeasureForm']
      },
      set (selection) {
        this.$shipBobStore.dispatch('pricing/setUnitOfMeasureForm', selection)
      }
    },
    originCountriesOptions () {
      const countries = this.$shipBobStore.getters['pricing/originCountries']
      return countries ? countries.options : []
    },
    selectedOriginCountry: {
      get () {
        return this.$shipBobStore.getters['pricing/selectedOriginCountry']
      },
      set (newOriginCountry) {
        this.$shipBobStore.dispatch('pricing/setSelectedOriginCountry', newOriginCountry)
      }
    },
    networkOptions () {
      const filteredNetworks = this.$shipBobStore.getters['pricing/filteredNetworks']
      if (!this.showNetworkOptionInitially && filteredNetworks.length !== 0) {
        this.setShowNetworkOptionInitially(true)
      }
      return filteredNetworks.length > 0 ? filteredNetworks : []
    },
    selectedNetwork: {
      get () {
        return this.$shipBobStore.getters['pricing/selectedNetwork']
      },
      set (newNetwork) {
        this.$shipBobStore.dispatch('pricing/setSelectedNetwork', newNetwork)
      }
    },
    showNetworkOption: {
      get () {
        return this.$shipBobStore.getters['pricing/showNetworkOption']
      }
    },
    hasMerchantPlusFCs () {
      return this.$shipBobStore.getters['pricing/filteredNetworks'].length > 0
    }
  },
  watch: {
    destinationCountries: {
      handler (newCountries) {
        this.filteredDestinationCountries = newCountries
      },
      immediate: true
    },
    shippingOptions: {
      handler (options) {
        this.filteredShippingOptions = options
      }
    },
    originCountries: {
      handler (originCountries) {
        this.originCountries = originCountries
      }
    },
    selectedOriginCountry: {
      handler (selectedOriginCountry) {
        this.selectedDestinationCountries = []
        this.filteredDestinationCountries = []
        this.destinationCountries = []
        this.filteredNetworks = []
        this.selectedNetwork = null
      }
    }
  },
  methods: {
    mapSelections (selections) {
      if (!selections || selections.length === 0) { return 'Please select a country' }
      return selections.map(s => s.name).join(', ')
    },
    mapOriginSelection (selection) {
      return selection ? selection.name : 'Please select a country'
    },
    handleFilterDestinationCountries ({ searchTerm, previousTerm }) {
      this.filteredDestinationCountries = this.destinationCountries.filter(country => {
        /*
                  The normalize will split accented character into the character and the accent.
                  The replace gets rid of the accent.
                */
        const unaccentedCountry = country.name.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
        const unaccentedSearchTerm = searchTerm.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
        return unaccentedCountry.indexOf(unaccentedSearchTerm) !== -1
      })
    },
    showZoneMapDownload (originCountry) {
      let showZoneMapDownload = false
      if (this.selectedOriginCountry != null) {
        const originCountryIso = this.selectedOriginCountry.countryISO
        if (originCountryIso === originCountry) {
          this.selectedDestinationCountries.forEach(destCountry => {
            if (destCountry.isoCode === originCountryIso) {
              showZoneMapDownload = true
            }
          })
        }
      }

      return showZoneMapDownload
    },
    setShowNetworkOptionInitially (showNetworkOptionInitially) {
      this.showNetworkOptionInitially = showNetworkOptionInitially
    }
  }
}
</script>
<style lang="scss" scoped>
@import '../../../../_colors';

a {
  text-decoration: none;
}

.pricing-selected-dropdown {
  input {
    color: $dropdown-grey
  }
}

.destination-countries-field {
  height: 100%;

  .destination-countries-search {
    height: 2.7125em;
  }
}

.container {
  max-width: 100% !important;
}

.container.sm, .container.md {
  .destination-countries-col {
    // margin-bottom: 1em;
    .destination-countries-field {
      margin-bottom: 0.52em;
    }
  }
   .box-type-row {
      margin-bottom: 0;
    }
  .unset-position {
    min-width: 100%;
  }
  .info-field {
    &:nth-child(1){
      margin-bottom: 1rem;
    }
    margin-right: 0;
  }
}

.container {
  .info-field {
    margin-right: 1rem;
  }

  .icon-question {
  margin-left: 0.2rem;
  margin-top: -0.2rem;
  }

  .shippingOptionField {
    .labelIconWrapper {
      display: inline-flex;
    }
  }

  label {
    padding-bottom: 0.50rem;
    font-size: 15px;
    font-weight: 600;
    color: $label-grey;
  }

  .no-height {
    height: 0;
  }
}

.sb-network-dropdown::v-deep .options-popover,
.shippingOptionDropdown::v-deep .options-popover{
    width: max-content;
    min-width: 100%;
}
.sb-network-dropdown::v-deep .options-popover {
  max-height: 20em !important;
}

.sb-network-dropdown::v-deep .nw-option:hover {
  background: none;
  .network-option {
    background: #EAEAEA;
  }
}
.sb-network-dropdown::v-deep .nw-option.selected {
  background: none;
  .network-option {
    background: #E2EAF3;
  }
}

.sb-form-field label {
  white-space: nowrap;
}

.sb-network-dropdown::v-deep .placeholder {
  white-space: nowrap;
}

.network-overview-tooltip {
    display: flex;
    max-width: 255px;
    max-height: 100px;
    background: #3E444E;
    border-radius: 4px;
    color: #fff;
    padding: 5px 15px;
    font-size: 14px;
    font-weight: 400;
    left: -138px !important;
    top: -3px !important;
    &:after{
        content: "";
        position: absolute;
        bottom: -19px;
        right: 98px;
        margin-left: -5px;
        border-width: 5px;
        border:10px solid transparent;
        border-bottom:10px solid #3E444E;
        transform: rotate(180deg);
      }
}

.network-option {
  background: #F7F7F7;
  box-sizing: border-box;
  border-radius: 4px;
  padding: 16px 13px 13px 13px;
}

.network-option-header {
  font-family: proxima-nova;
  font-style: normal;
  font-size: 14px;
  font-weight: 600;
  line-height: 14px;
  letter-spacing: 0px;
  text-align: left;
  color: #2470AF;
  padding-bottom: 2px;
}

.network-option-fc-list {
  font-family: proxima-nova;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 12px;
  letter-spacing: 0px;
  text-align: left;
  color: #3D444F;
  padding-top: 6px;
}

</style>
